import {
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { AGUAppModuleAccent } from '../../app-surface/app-module-definition';

/** A file attached to a search hero query, tracked through its upload lifecycle. */
export interface AGUSearchAttachment {
    Name: string;
    Status: 'uploading' | 'ready' | 'failed';
    Error?: string;
}

/** The mockup's resting field height (`rows="3"` / `min-height: 60px`, parity/grants.md §4.2). */
const MIN_FIELD_HEIGHT_PX = 60;

/** Tallest the field grows before it scrolls — roughly eight lines at the mockup's 15px/1.5. */
const MAX_FIELD_HEIGHT_PX = 180;

/** Makes each instance's label/field id pair unique, so two heroes on one screen never collide. */
let nextFieldId = 0;

/** The submit button's one visual/label state. A search in flight (`busy`) always wins over `applied`: Job Finder sets `Applied` on submit, so both are true while the request runs. */
export type HeroSubmitStateName = 'busy' | 'applied' | 'idle';

export function HeroSubmitState(busy: boolean, applied: boolean): HeroSubmitStateName {
    if (busy) return 'busy';
    return applied ? 'applied' : 'idle';
}

/**
 * The portal's search card (`.card.searchcard` in every tool mockup): an
 * uppercase eyebrow that IS the field's label, an 8px-radius box holding a
 * magnifier and the describe-it field, an action bar carrying the attach control
 * and the accent submit, and a `Try` row of suggestion pills.
 *
 * Shared by every finder module because all three ask the same shape of question
 * — free text describing what the member is looking for — and the design gives
 * that question one recognisable treatment. Result presentation is where the
 * modules differ, so it is deliberately not part of this component.
 *
 * The field is a <textarea>, not an <input>: members paste abstracts, project
 * summaries and job descriptions into it. It shows ONE row (the mockup's search
 * card is a single-line box until it has something to hold) and grows as the
 * member types or adds lines with Shift+Enter, up to MAX_FIELD_HEIGHT_PX.
 */
@Component({
    standalone: false,
    selector: 'agu-search-hero',
    templateUrl: './search-hero.component.html',
    styleUrl: './search-hero.component.scss',
})
export class AGUSearchHeroComponent implements OnChanges {
    @Input({ required: true }) Placeholder!: string;
    @Input() SubmitLabel = 'Search';
    /** Disables submit and shows the busy label while a search is in flight. */
    @Input() Busy = false;
    @Input() BusyLabel = 'Searching…';
    /** Two-way bound query text. */
    @Input() Query = '';
    /** Uppercase label above the box, e.g. 'Search grants'. It labels the field, so it is a real <label>. */
    @Input() Eyebrow: string | null = null;
    /** Label for the attach-a-file control, e.g. "Attach a document". Null hides the control entirely. */
    @Input() AttachLabel: string | null = null;
    @Input() AttachHint = 'PDF, DOCX or TXT';
    /** The currently attached file, or null when none is attached. */
    @Input() Attachment: AGUSearchAttachment | null = null;
    /**
     * "Try" chips rendered below the card. Pass none and project an element carrying the `TryRow` attribute to replace the whole row
     * with host-owned content (the label is the host's too: `Eyebrow` already overrides it).
     */
    @Input() Suggestions: readonly string[] = [];
    /** Plain Enter submits (the mockup); Shift+Enter still inserts a newline. */
    @Input() SubmitOnEnter = true;
    /**
     * The search that produced the results on screen has been applied (P/job-finder.html:1546-1551): the submit reads "Results shown",
     * carries a check and does nothing until the query changes. The page sets it after a submit and clears it on `QueryChange`.
     */
    @Input() Applied = false;
    /** Leads the attachment chip with this label and a check — "CV attached" on the Jobs finder (P/job-finder.html:1134). Null keeps the plain file chip. */
    @Input() AttachedLabel: string | null = null;
    /** The module's colour; blue is the portal default. */
    @Input() Accent: AGUAppModuleAccent = 'blue';

    @Output() QueryChange = new EventEmitter<string>();
    @Output() Submitted = new EventEmitter<string>();
    @Output() FileSelected = new EventEmitter<File>();
    @Output() AttachmentRemoved = new EventEmitter<void>();
    @Output() SuggestionSelected = new EventEmitter<string>();

    @ViewChild('fileInput') FileInput?: ElementRef<HTMLInputElement>;
    @ViewChild('field') private Field?: ElementRef<HTMLTextAreaElement>;

    /** Ties the eyebrow <label> to the field. */
    readonly FieldId = `agu-hero-field-${(nextFieldId += 1)}`;

    /** The button's label/tint state; a search in flight wins over applied. */
    get SubmitState(): HeroSubmitStateName {
        return HeroSubmitState(this.Busy, this.Applied);
    }

    /**
     * Whether submit is available. Never on a whitespace-only query with
     * NOTHING else to search on — but a ready attachment IS something to search
     * on: the document supplies the embedded text and the intake then runs on an
     * empty typed string (design D11). Before this, attaching a CV and pressing
     * Search did nothing at all, because the button stayed disabled
     * (Task 15 round 1, I-1/I-3).
     */
    get CanSubmit(): boolean {
        if (this.Busy || this.Applied) {
            return false;
        }
        return this.Query.trim().length > 0 || this.Attachment?.Status === 'ready';
    }

    /**
     * A query set from outside (a `Try` pill, a restored search) has to resize
     * the field too, and at this point the element still holds the PREVIOUS
     * value — the `[value]` binding is written later in the same change-detection
     * pass. Writing it here first makes the measurement honest; the binding then
     * writes the identical string.
     */
    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['Query']) {
            return;
        }
        const field = this.Field?.nativeElement;
        if (!field) {
            return;
        }
        field.value = this.Query;
        this.GrowField(field);
    }

    OnQueryInput(value: string, field: HTMLTextAreaElement): void {
        this.Query = value;
        this.QueryChange.emit(value);
        this.GrowField(field);
    }

    /** The mockup's `.sclear` bubble: empties the field without submitting anything. */
    ClearQuery(): void {
        this.Query = '';
        this.QueryChange.emit('');
        const field = this.Field?.nativeElement;
        if (field) {
            field.value = '';
            this.GrowField(field);
        }
    }

    Submit(): void {
        if (!this.CanSubmit) {
            return;
        }
        this.Submitted.emit(this.Query.trim());
    }

    /**
     * Plain Enter submits when `SubmitOnEnter` is true (the mockup default);
     * Shift+Enter always inserts a newline instead, so multi-paragraph pastes
     * stay editable. Ctrl/Cmd+Enter always submits regardless of `SubmitOnEnter`
     * — it is the deliberate "I'm done, go" chord.
     */
    OnKeydown(event: KeyboardEvent): void {
        if (event.key !== 'Enter') {
            return;
        }
        if (event.metaKey || event.ctrlKey) {
            event.preventDefault();
            this.Submit();
            return;
        }
        if (this.SubmitOnEnter && !event.shiftKey) {
            event.preventDefault();
            this.Submit();
        }
    }

    OpenFilePicker(): void {
        this.FileInput?.nativeElement.click();
    }

    OnFileChosen(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            this.FileSelected.emit(file);
        }
        // Reset so choosing the same file again still fires a change event.
        input.value = '';
    }

    RemoveAttachment(): void {
        this.AttachmentRemoved.emit();
    }

    SelectSuggestion(suggestion: string): void {
        this.SuggestionSelected.emit(suggestion);
    }

    /**
     * One row that grows with its content. Done in the event handler rather than
     * with a timer or an AfterViewChecked hook: this host is zoneless, and a
     * measurement taken during the DOM event that caused the change is both the
     * cheapest and the only one guaranteed to see the new text.
     */
    private GrowField(field: HTMLTextAreaElement): void {
        field.style.height = 'auto';
        const clamped = Math.min(Math.max(field.scrollHeight, MIN_FIELD_HEIGHT_PX), MAX_FIELD_HEIGHT_PX);
        field.style.height = `${clamped}px`;
    }
}
